import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import { Coins, Flame, Trophy, Medal, Crown } from "lucide-react";

const RANK_ICONS = [Crown, Medal, Trophy];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mwz/leaderboard");
        setRows(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10">
      <BackButton />
      <div className="mt-5 mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          /leaderboard
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
          MWZ Leaderboard
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Top earners across the academy. Climb the ranks by completing modules, courses and keeping your streak alive.
        </p>
      </div>

      {/* Top 3 podium */}
      {rows.length >= 3 && (
        <div className="mb-10 grid grid-cols-3 gap-3">
          {[rows[1], rows[0], rows[2]].map((r, i) => {
            const realRank = i === 0 ? 2 : i === 1 ? 1 : 3;
            const height = realRank === 1 ? "h-44" : realRank === 2 ? "h-36" : "h-32";
            const bg = realRank === 1 ? "bg-foreground text-background" : realRank === 2 ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground";
            const Icon = RANK_ICONS[realRank - 1];
            return (
              <div key={r.id} className="flex flex-col items-center justify-end" data-testid={`podium-${realRank}`}>
                <div className="mb-2 text-center">
                  {r.avatar ? (
                    <img src={r.avatar} alt={r.name} className="mx-auto h-14 w-14 border-2 border-foreground object-cover" />
                  ) : (
                    <div className="mx-auto flex h-14 w-14 items-center justify-center border-2 border-foreground bg-muted">
                      {r.name?.[0]}
                    </div>
                  )}
                  <div className="font-display mt-2 text-sm font-extrabold tracking-tight">
                    {r.name}
                  </div>
                </div>
                <div className={`relative w-full ${bg} ${height} flex flex-col items-center justify-end p-4 border-2 border-foreground hard-shadow-sm`}>
                  <Icon className="absolute top-3 right-3 h-5 w-5 opacity-70" />
                  <div className="font-display text-4xl font-extrabold tracking-tighter">
                    #{realRank}
                  </div>
                  <div className="mono mt-1 flex items-center gap-1 text-[11px] uppercase tracking-widest">
                    <Coins className="h-3 w-3" />
                    {r.mwz_balance.toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border-2 border-foreground bg-background">
        <div className="mono grid grid-cols-12 gap-2 border-b border-border bg-muted px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div className="col-span-1">Rank</div>
          <div className="col-span-6">Student</div>
          <div className="col-span-2 text-right">Streak</div>
          <div className="col-span-3 text-right">MWZ</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No data yet — be the first to earn MWZ!
          </div>
        ) : (
          rows.map((r, i) => {
            const isYou = r.id === user?.id;
            return (
              <div
                key={r.id}
                className={`grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-b-0 ${isYou ? "bg-primary/10" : ""}`}
              >
                <div className="col-span-1 font-display text-xl font-extrabold">
                  {i + 1}
                </div>
                <div className="col-span-6 flex items-center gap-3">
                  {r.avatar ? (
                    <img src={r.avatar} alt={r.name} className="h-8 w-8 border border-foreground object-cover" />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center border border-foreground bg-muted text-xs font-bold">
                      {r.name?.[0]}
                    </div>
                  )}
                  <div className="font-semibold">
                    {r.name}
                    {isYou && <span className="mono ml-2 bg-primary px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-primary-foreground">You</span>}
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <span className="mono inline-flex items-center gap-1 text-[10px]">
                    <Flame className="h-3 w-3 text-accent" />
                    {r.streak_days}d
                  </span>
                </div>
                <div className="col-span-3 text-right font-mono font-semibold">
                  {r.mwz_balance.toLocaleString()} MWZ
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
